import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSpinner, 
  faEdit, 
  faTrash, 
  faPlus, 
  faSync, 
  faTimes, 
  faShare, 
  faCopy, 
  faCheck 
} from '@fortawesome/free-solid-svg-icons';
import { 
  faWhatsapp, 
  faFacebook, 
  faTwitter, 
  faLinkedin 
} from '@fortawesome/free-brands-svg-icons';
import api, { 
  getAllWheels, 
  deleteWheel, 
  createWheel, 
  updateWheel, 
  CreateWheelRequest, 
  UpdateWheelRequest,
  getWheel
} from '../../services/adminApi.ts';

const Container = styled.div`
  padding: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  color: #2d3748;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
`;

const Button = styled.button`
  padding: 0.5rem 1rem;
  border-radius: 0.25rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const AddButton = styled(Button)`
  background-color: #4299e1;
  color: white;
  border: none;
  
  &:hover {
    background-color: #3182ce;
  }
`;

const RefreshButton = styled(Button)`
  background-color: white;
  color: #4a5568;
  border: 1px solid #e2e8f0;
  
  &:hover {
    background-color: #f7fafc;
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const SearchContainer = styled.div`
  margin-bottom: 1.5rem;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.25rem;
  font-size: 0.875rem;
  
  &:focus {
    outline: none;
    border-color: #4299e1;
    box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.2);
  }
`;

const TableContainer = styled.div`
  overflow-x: auto;
  position: relative;
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: white;
  padding: 2rem;
  border-radius: 8px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  font-size: 1rem;
  color: #718096;
  cursor: pointer;
  
  &:hover {
    color: #2d3748;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 500;
  color: #4a5568;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  font-size: 0.875rem;
  
  &:focus {
    outline: none;
    border-color: #4299e1;
    box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.2);
  }
`;

const TextArea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  font-size: 0.875rem;
  min-height: 100px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #4299e1;
    box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.2);
  }
`;

const Checkbox = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SubmitButton = styled.button`
  background-color: #4299e1;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  margin-top: 1rem;
  
  &:hover {
    background-color: #3182ce;
  }
  
  &:disabled {
    background-color: #a0aec0;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: #e53e3e;
  font-size: 0.875rem;
  margin-top: 0.5rem;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  font-size: 1.125rem;
  color: #4a5568;
  gap: 0.5rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 0;
  color: #4a5568;
  font-size: 1.125rem;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 0.75rem 1rem;
  border-bottom: 2px solid #e2e8f0;
  color: #4a5568;
  font-weight: 600;
  font-size: 0.875rem;
`;

const Td = styled.td`
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #e2e8f0;
  color: #2d3748;
  font-size: 0.875rem;
`;

const StatusIndicator = styled.span<{ status: string }>`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
  background-color: ${props => {
    // Ensure status is a string before calling toLowerCase
    const statusValue = typeof props.status === 'string' ? props.status.toLowerCase() : '';
    switch (statusValue) {
      case 'active':
        return '#C6F6D5';
      case 'inactive':
        return '#FED7D7';
      case 'pending':
        return '#FEEBC8';
      default:
        return '#E2E8F0';
    }
  }};
  color: ${props => {
    // Ensure status is a string before calling toLowerCase
    const statusValue = typeof props.status === 'string' ? props.status.toLowerCase() : '';
    switch (statusValue) {
      case 'active':
        return '#22543D';
      case 'inactive':
        return '#822727';
      case 'pending':
        return '#744210';
      default:
        return '#2D3748';
    }
  }};
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  color: #4299e1;
  cursor: pointer;
  margin-right: 0.5rem;
  
  &:hover {
    color: #3182ce;
  }
  
  &.delete {
    color: #e53e3e;
    
    &:hover {
      color: #c53030;
    }
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
`;

const SuccessMessage = styled.div`
  color: #2F855A;
  background-color: #C6F6D5;
  padding: 0.75rem;
  border-radius: 0.25rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CloseSuccessButton = styled.button`
  background: none;
  border: none;
  color: #2F855A;
  cursor: pointer;
  font-size: 1rem;
`;

// Add new styled components for the share modal
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ShareModal = styled.div`
  background-color: white;
  border-radius: 0.5rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  width: 90%;
  max-width: 500px;
  padding: 1.5rem;
  position: relative;
`;

const ShareTitle = styled.h3`
  margin-bottom: 1.5rem;
  color: #2d3748;
  font-size: 1.25rem;
`;

const ShareLink = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
  background-color: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 0.375rem;
  padding: 0.5rem;
`;

const ShareInput = styled.input`
  flex: 1;
  border: none;
  background: transparent;
  padding: 0.5rem;
  font-size: 0.875rem;
  color: #4a5568;
  &:focus {
    outline: none;
  }
`;

const CopyButton = styled.button`
  background-color: #4299e1;
  color: white;
  border: none;
  border-radius: 0.25rem;
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #3182ce;
  }
`;

const ShareOptions = styled.div`
  display: flex;
  justify-content: space-around;
  margin-top: 1.5rem;
`;

const ShareOption = styled.a`
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #4a5568;
  text-decoration: none;
  cursor: pointer;
  
  &:hover {
    color: #2d3748;
  }
  
  svg {
    font-size: 1.5rem;
    margin-bottom: 0.5rem;
  }
  
  &.whatsapp svg {
    color: #25D366;
  }
  
  &.facebook svg {
    color: #1877F2;
  }
  
  &.twitter svg {
    color: #1DA1F2;
  }
  
  &.linkedin svg {
    color: #0A66C2;
  }
`;

const ShareLabel = styled.span`
  font-size: 0.75rem;
`;

// Add a new styled component for the registration fields section
const RegistrationFieldsSection = styled.div`
  margin-top: 1.5rem;
  padding: 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.25rem;
  background-color: #f8fafc;
`;

const FieldsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-top: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FieldItem = styled.div`
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.25rem;
  background-color: white;
`;

const FieldHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const FieldLabel = styled.div`
  font-weight: 500;
`;

const FieldOptions = styled.div`
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px dashed #e2e8f0;
  font-size: 0.875rem;
`;

const OptionsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.25rem;
`;

const OptionTag = styled.span`
  padding: 0.25rem 0.5rem;
  background-color: #edf2f7;
  border-radius: 0.25rem;
  font-size: 0.75rem;
`;

interface Wheel {
  id: string;
  name: string;
  status: string;
  slotCount: number;
  createdAt: string;
  businessUserId: string | null;
  isPrivate: boolean;
}

interface WheelFormData {
  name: string;
  description: string;
  slotCount: number;
  isPrivate: boolean;
  expiresAt?: string;
  accessCode?: string;
  price?: number;
  rules?: string;
  guidelines?: string;
  registrationFields?: {
    enabled: boolean;
    fields: RegistrationField[];
  };
}

interface RegistrationField {
  id: string;
  name: string;
  label: string;
  type: 'text' | 'number' | 'select' | 'checkbox';
  required: boolean;
  options?: string[];
  enabled: boolean;
}

// Default registration fields
const defaultRegistrationFields: RegistrationField[] = [
  { 
    id: 'username', 
    name: 'username', 
    label: 'Username', 
    type: 'text', 
    required: true, 
    enabled: false 
  },
  { 
    id: 'age', 
    name: 'age', 
    label: 'Age', 
    type: 'number', 
    required: true, 
    enabled: true 
  },
  { 
    id: 'gender', 
    name: 'gender', 
    label: 'Gender', 
    type: 'select', 
    required: true, 
    options: ['male', 'female', 'other'], 
    enabled: true 
  },
  { 
    id: 'interest', 
    name: 'interest', 
    label: 'Interests', 
    type: 'text', 
    required: false, 
    enabled: false 
  },
  { 
    id: 'bio', 
    name: 'bio', 
    label: 'Short Bio', 
    type: 'text', 
    required: false, 
    enabled: false 
  },
  { 
    id: 'identity', 
    name: 'identity', 
    label: 'Who am I', 
    type: 'select', 
    required: false, 
    options: ['student', 'expat', 'traveller', 'professional', 'other'],
    enabled: false 
  }
];

const AdminWheels: React.FC = () => {
  const [wheels, setWheels] = useState<Wheel[]>([]);
  const [filteredWheels, setFilteredWheels] = useState<Wheel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentWheel, setCurrentWheel] = useState<Wheel | null>(null);
  const [formData, setFormData] = useState<WheelFormData>({
    name: '',
    description: '',
    slotCount: 12,
    isPrivate: false,
    accessCode: '',
    price: undefined,
    rules: '',
    guidelines: '',
    registrationFields: {
      enabled: true,
      fields: JSON.parse(JSON.stringify(defaultRegistrationFields)) // Deep copy
    }
  });
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Add new state variables for sharing
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareWheel, setShareWheel] = useState<Wheel | null>(null);
  const [copied, setCopied] = useState(false);

  const fetchWheels = async () => {
    try {
      setLoading(true);
      // Use the admin wheels endpoint
      const response = await getAllWheels();
      
      // Map the response to match our Wheel interface
      const mappedWheels = response.map((wheel: any) => ({
        id: wheel.id,
        name: wheel.name,
        status: wheel.status,
        slotCount: wheel.slotCount,
        createdAt: wheel.createdAt,
        businessUserId: wheel.businessUserId || null,
        isPrivate: wheel.isPrivate,
      }));
      
      setWheels(mappedWheels);
      setFilteredWheels(mappedWheels);
      setLoading(false);
      setRefreshing(false);
    } catch (err) {
      console.error('Error fetching wheels:', err);
      setError('Failed to load wheels data');
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchWheels();
  }, []);

  useEffect(() => {
    // Filter wheels based on search term
    if (searchTerm.trim() === '') {
      setFilteredWheels(wheels);
    } else {
      const filtered = wheels.filter(wheel => 
        wheel.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredWheels(filtered);
    }
  }, [searchTerm, wheels]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchWheels();
  };

  const handleEdit = (id: string) => {
    const wheel = wheels.find(w => w.id === id);
    if (wheel) {
      setCurrentWheel(wheel);
      // Fetch the full wheel details
      getWheelDetails(id);
    }
  };

  const getWheelDetails = async (id: string) => {
    try {
      const wheelData = await getWheel(id);
      
      setFormData({
        name: wheelData.name,
        description: wheelData.description,
        slotCount: wheelData.slotCount,
        isPrivate: wheelData.isPrivate,
        expiresAt: wheelData.expiresAt ? new Date(wheelData.expiresAt).toISOString().split('T')[0] : undefined,
        accessCode: wheelData.accessCode || '',
        price: wheelData.price,
        rules: wheelData.rules || '',
        guidelines: wheelData.guidelines || '',
        registrationFields: {
          enabled: wheelData.registrationFields?.enabled || false,
          fields: wheelData.registrationFields?.fields || []
        }
      });
      
      setShowEditModal(true);
    } catch (err) {
      console.error('Error fetching wheel details:', err);
      alert('Failed to load wheel details. Please try again.');
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this wheel?')) {
      try {
        const wheelToDelete = wheels.find(wheel => wheel.id === id);
        const wheelName = wheelToDelete ? wheelToDelete.name : 'Wheel';
        
        await deleteWheel(id);
        
        // Remove the deleted wheel from the state
        setWheels(wheels.filter(wheel => wheel.id !== id));
        setFilteredWheels(filteredWheels.filter(wheel => wheel.id !== id));
        
        setSuccessMessage(`Wheel "${wheelName}" was successfully deleted.`);
        
        // Clear success message after 5 seconds
        setTimeout(() => {
          setSuccessMessage(null);
        }, 5000);
      } catch (err) {
        console.error('Error deleting wheel:', err);
        alert('Failed to delete wheel. Please try again.');
      }
    }
  };

  const handleAddNew = () => {
    // Reset form data
    setFormData({
      name: '',
      description: '',
      slotCount: 12,
      isPrivate: false,
      accessCode: '',
      price: undefined,
      rules: '',
      guidelines: '',
      registrationFields: {
        enabled: true,
        fields: JSON.parse(JSON.stringify(defaultRegistrationFields)) // Deep copy
      }
    });
    setFormError(null);
    setShowAddModal(true);
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else if (type === 'number') {
      setFormData(prev => ({ ...prev, [name]: value === '' ? undefined : Number(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmitAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    
    if (!formData.name.trim()) {
      setFormError('Name is required');
      return;
    }
    
    if (!formData.description.trim()) {
      setFormError('Description is required');
      return;
    }
    
    try {
      setSubmitting(true);
      
      const wheelData: CreateWheelRequest = {
        name: formData.name,
        description: formData.description,
        slotCount: formData.slotCount,
        isPrivate: formData.isPrivate,
        expiresAt: formData.expiresAt ? new Date(formData.expiresAt + 'T00:00:00Z').toISOString() : undefined,
        accessCode: formData.accessCode || undefined,
        price: formData.price,
        rules: formData.rules || undefined,
        guidelines: formData.guidelines || undefined,
        registrationFields: JSON.stringify(formData.registrationFields)
      };
      
      const response = await createWheel(wheelData);
      
      // Add the new wheel to the state
      const newWheel: Wheel = {
        id: response.id,
        name: response.name,
        status: response.status,
        slotCount: response.slotCount,
        createdAt: response.createdAt,
        businessUserId: response.businessUserId || null,
        isPrivate: response.isPrivate,
      };
      
      setWheels(prev => [newWheel, ...prev]);
      setFilteredWheels(prev => [newWheel, ...prev]);
      
      setShowAddModal(false);
      setSubmitting(false);
      setSuccessMessage(`Wheel "${response.name}" was successfully created.`);
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
    } catch (err: any) {
      console.error('Error creating wheel:', err);
      setFormError(err.response?.data?.message || 'Failed to create wheel. Please try again.');
      setSubmitting(false);
    }
  };

  const handleSubmitEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    
    if (!currentWheel) return;
    
    if (!formData.name.trim()) {
      setFormError('Name is required');
      return;
    }
    
    if (!formData.description.trim()) {
      setFormError('Description is required');
      return;
    }
    
    try {
      setSubmitting(true);
      
      const wheelData: UpdateWheelRequest = {
        name: formData.name,
        description: formData.description,
        isPrivate: formData.isPrivate,
        expiresAt: formData.expiresAt ? new Date(formData.expiresAt + 'T00:00:00Z').toISOString() : undefined,
        accessCode: formData.accessCode || undefined,
        price: formData.price,
        rules: formData.rules || undefined,
        guidelines: formData.guidelines || undefined,
        registrationFields: JSON.stringify(formData.registrationFields)
      };
      
      const response = await updateWheel(currentWheel.id, wheelData);
      
      // Update the wheel in the state with all updated properties
      const updatedWheels = wheels.map(wheel => 
        wheel.id === currentWheel.id 
          ? { 
              ...wheel, 
              name: response.name,
              status: response.status,
              slotCount: response.slotCount,
              businessUserId: response.businessUserId || null,
              isPrivate: response.isPrivate
            } 
          : wheel
      );
      
      setWheels(updatedWheels);
      setFilteredWheels(updatedWheels.filter(wheel => 
        wheel.name.toLowerCase().includes(searchTerm.toLowerCase())
      ));
      
      setShowEditModal(false);
      setSubmitting(false);
      setSuccessMessage(`Wheel "${response.name}" was successfully updated.`);
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
    } catch (err: any) {
      console.error('Error updating wheel:', err);
      setFormError(err.response?.data?.message || 'Failed to update wheel. Please try again.');
      setSubmitting(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleShare = (wheel: Wheel) => {
    setShareWheel(wheel);
    setShowShareModal(true);
    setCopied(false);
  };
  
  const copyToClipboard = () => {
    if (!shareWheel) return;
    
    const wheelUrl = `${window.location.origin}/wheel/${shareWheel.id}`;
    navigator.clipboard.writeText(wheelUrl)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(err => {
        console.error('Failed to copy: ', err);
        alert('Failed to copy to clipboard');
      });
  };
  
  const getShareUrl = (platform: string) => {
    if (!shareWheel) return '#';
    
    const wheelUrl = encodeURIComponent(`${window.location.origin}/wheel/${shareWheel.id}`);
    const wheelName = encodeURIComponent(shareWheel.name);
    
    switch (platform) {
      case 'whatsapp':
        return `https://wa.me/?text=Check out this wheel: ${wheelName} ${wheelUrl}`;
      case 'facebook':
        return `https://www.facebook.com/sharer/sharer.php?u=${wheelUrl}`;
      case 'twitter':
        return `https://twitter.com/intent/tweet?text=Check out this wheel: ${wheelName}&url=${wheelUrl}`;
      case 'linkedin':
        return `https://www.linkedin.com/sharing/share-offsite/?url=${wheelUrl}`;
      default:
        return '#';
    }
  };

  // Add a handler for registration field toggling
  const handleRegistrationFieldToggle = (fieldId: string) => {
    setFormData(prev => {
      const updatedFields = prev.registrationFields!.fields.map(field => {
        if (field.id === fieldId) {
          return { ...field, enabled: !field.enabled };
        }
        return field;
      });
      
      return {
        ...prev,
        registrationFields: {
          ...prev.registrationFields!,
          fields: updatedFields
        }
      };
    });
  };

  // Add a handler for toggling all registration fields
  const handleToggleAllRegistrationFields = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      registrationFields: {
        ...prev.registrationFields!,
        enabled: e.target.checked
      }
    }));
  };

  if (loading) {
    return (
      <Container>
        <Header>
          <Title>Wheels Management</Title>
        </Header>
        <LoadingContainer>
          <FontAwesomeIcon icon={faSpinner} spin /> Loading...
        </LoadingContainer>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Header>
          <Title>Wheels Management</Title>
        </Header>
        <EmptyState>{error}</EmptyState>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>Wheels Management</Title>
        <ButtonGroup>
          <RefreshButton onClick={handleRefresh} disabled={refreshing}>
            <FontAwesomeIcon icon={faSync} spin={refreshing} /> {refreshing ? 'Refreshing...' : 'Refresh'}
          </RefreshButton>
          <AddButton onClick={handleAddNew}>
            <FontAwesomeIcon icon={faPlus} /> Add New Wheel
          </AddButton>
        </ButtonGroup>
      </Header>

      {successMessage && (
        <SuccessMessage>
          <span>{successMessage}</span>
          <CloseSuccessButton onClick={() => setSuccessMessage(null)}>
            <FontAwesomeIcon icon={faTimes} />
          </CloseSuccessButton>
        </SuccessMessage>
      )}

      <SearchContainer>
        <SearchInput 
          type="text" 
          placeholder="Search by wheel name..." 
          value={searchTerm}
          onChange={handleSearchChange}
        />
      </SearchContainer>

      {filteredWheels.length === 0 ? (
        <EmptyState>{searchTerm ? 'No wheels match your search' : 'No wheels found'}</EmptyState>
      ) : (
        <TableContainer>
          {refreshing && (
            <LoadingOverlay>
              <FontAwesomeIcon icon={faSpinner} spin size="2x" />
            </LoadingOverlay>
          )}
          <Table>
            <thead>
              <tr>
                <Th>Name</Th>
                <Th>Status</Th>
                <Th>Slots</Th>
                <Th>Created</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {filteredWheels.map((wheel) => (
                <tr key={wheel.id}>
                  <Td>{wheel.name}</Td>
                  <Td>
                    <StatusIndicator status={wheel.status}>
                      {wheel.status}
                    </StatusIndicator>
                  </Td>
                  <Td>{wheel.slotCount}</Td>
                  <Td>{formatDate(wheel.createdAt)}</Td>
                  <Td>
                    <ActionButton onClick={() => handleEdit(wheel.id)}>
                      <FontAwesomeIcon icon={faEdit} />
                    </ActionButton>
                    <ActionButton className="delete" onClick={() => handleDelete(wheel.id)}>
                      <FontAwesomeIcon icon={faTrash} />
                    </ActionButton>
                    {wheel.isPrivate && (
                      <ActionButton className="share" onClick={() => handleShare(wheel)}>
                        <FontAwesomeIcon icon={faShare} />
                      </ActionButton>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      )}

      {/* Add Wheel Modal */}
      {showAddModal && (
        <Modal>
          <ModalContent>
            <CloseButton onClick={() => setShowAddModal(false)}>
              <FontAwesomeIcon icon={faTimes} />
            </CloseButton>
            <h2>Add New Wheel</h2>
            <Form onSubmit={handleSubmitAdd}>
              <FormGroup>
                <Label htmlFor="name">Name *</Label>
                <Input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleFormChange}
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="description">Description *</Label>
                <TextArea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleFormChange}
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="slotCount">Number of Slots *</Label>
                <Input
                  type="number"
                  id="slotCount"
                  name="slotCount"
                  min="2"
                  max="100"
                  value={formData.slotCount}
                  onChange={handleFormChange}
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <Checkbox>
                  <Input
                    type="checkbox"
                    id="isPrivate"
                    name="isPrivate"
                    checked={formData.isPrivate}
                    onChange={handleFormChange}
                  />
                  <Label htmlFor="isPrivate">Private Wheel</Label>
                </Checkbox>
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="expiresAt">Expiration Date</Label>
                <Input
                  type="date"
                  id="expiresAt"
                  name="expiresAt"
                  value={formData.expiresAt || ''}
                  onChange={handleFormChange}
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="accessCode">Access Code</Label>
                <Input
                  type="text"
                  id="accessCode"
                  name="accessCode"
                  value={formData.accessCode || ''}
                  onChange={handleFormChange}
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="price">Price</Label>
                <Input
                  type="number"
                  id="price"
                  name="price"
                  step="0.01"
                  min="0"
                  value={formData.price || ''}
                  onChange={handleFormChange}
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="rules">Rules</Label>
                <TextArea
                  id="rules"
                  name="rules"
                  value={formData.rules || ''}
                  onChange={handleFormChange}
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="guidelines">Guidelines</Label>
                <TextArea
                  id="guidelines"
                  name="guidelines"
                  value={formData.guidelines || ''}
                  onChange={handleFormChange}
                />
              </FormGroup>
              
              {/* Registration Fields Section */}
              <RegistrationFieldsSection>
                <FormGroup>
                  <Checkbox>
                    <Input
                      type="checkbox"
                      id="enableRegistrationFields"
                      name="enableRegistrationFields"
                      checked={formData.registrationFields?.enabled}
                      onChange={handleToggleAllRegistrationFields}
                    />
                    <Label htmlFor="enableRegistrationFields">Enable Custom Registration Fields</Label>
                  </Checkbox>
                </FormGroup>
                
                {formData.registrationFields?.enabled && (
                  <>
                    <p>Configure which fields users must fill when registering to join this wheel:</p>
                    <FieldsGrid>
                      {formData.registrationFields.fields.map(field => (
                        <FieldItem key={field.id}>
                          <FieldHeader>
                            <FieldLabel>{field.label}</FieldLabel>
                            <Checkbox>
                              <Input
                                type="checkbox"
                                id={`field-${field.id}`}
                                checked={field.enabled}
                                onChange={() => handleRegistrationFieldToggle(field.id)}
                              />
                              <Label htmlFor={`field-${field.id}`}>Enable</Label>
                            </Checkbox>
                          </FieldHeader>
                          <div>Type: {field.type}</div>
                          {field.required && <div>Required: Yes</div>}
                          
                          {field.options && field.options.length > 0 && (
                            <FieldOptions>
                              <div>Options:</div>
                              <OptionsList>
                                {field.options.map(option => (
                                  <OptionTag key={option}>{option}</OptionTag>
                                ))}
                              </OptionsList>
                            </FieldOptions>
                          )}
                        </FieldItem>
                      ))}
                    </FieldsGrid>
                  </>
                )}
              </RegistrationFieldsSection>
              
              {formError && <ErrorMessage>{formError}</ErrorMessage>}
              
              <SubmitButton type="submit" disabled={submitting}>
                {submitting ? 'Creating...' : 'Create Wheel'}
              </SubmitButton>
            </Form>
          </ModalContent>
        </Modal>
      )}

      {/* Edit Wheel Modal */}
      {showEditModal && currentWheel && (
        <Modal>
          <ModalContent>
            <CloseButton onClick={() => setShowEditModal(false)}>
              <FontAwesomeIcon icon={faTimes} />
            </CloseButton>
            <h2>Edit Wheel: {currentWheel.name}</h2>
            <Form onSubmit={handleSubmitEdit}>
              <FormGroup>
                <Label htmlFor="edit-name">Name *</Label>
                <Input
                  type="text"
                  id="edit-name"
                  name="name"
                  value={formData.name}
                  onChange={handleFormChange}
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="edit-description">Description *</Label>
                <TextArea
                  id="edit-description"
                  name="description"
                  value={formData.description}
                  onChange={handleFormChange}
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <Checkbox>
                  <Input
                    type="checkbox"
                    id="edit-isPrivate"
                    name="isPrivate"
                    checked={formData.isPrivate}
                    onChange={handleFormChange}
                  />
                  <Label htmlFor="edit-isPrivate">Private Wheel</Label>
                </Checkbox>
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="edit-expiresAt">Expiration Date</Label>
                <Input
                  type="date"
                  id="edit-expiresAt"
                  name="expiresAt"
                  value={formData.expiresAt || ''}
                  onChange={handleFormChange}
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="edit-accessCode">Access Code</Label>
                <Input
                  type="text"
                  id="edit-accessCode"
                  name="accessCode"
                  value={formData.accessCode || ''}
                  onChange={handleFormChange}
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="edit-price">Price</Label>
                <Input
                  type="number"
                  id="edit-price"
                  name="price"
                  step="0.01"
                  min="0"
                  value={formData.price || ''}
                  onChange={handleFormChange}
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="edit-rules">Rules</Label>
                <TextArea
                  id="edit-rules"
                  name="rules"
                  value={formData.rules || ''}
                  onChange={handleFormChange}
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="edit-guidelines">Guidelines</Label>
                <TextArea
                  id="edit-guidelines"
                  name="guidelines"
                  value={formData.guidelines || ''}
                  onChange={handleFormChange}
                />
              </FormGroup>
              
              {/* Registration Fields Section */}
              <RegistrationFieldsSection>
                <FormGroup>
                  <Checkbox>
                    <Input
                      type="checkbox"
                      id="edit-enableRegistrationFields"
                      name="enableRegistrationFields"
                      checked={formData.registrationFields?.enabled}
                      onChange={handleToggleAllRegistrationFields}
                    />
                    <Label htmlFor="edit-enableRegistrationFields">Enable Custom Registration Fields</Label>
                  </Checkbox>
                </FormGroup>
                
                {formData.registrationFields?.enabled && (
                  <>
                    <p>Configure which fields users must fill when registering to join this wheel:</p>
                    <FieldsGrid>
                      {formData.registrationFields.fields.map(field => (
                        <FieldItem key={field.id}>
                          <FieldHeader>
                            <FieldLabel>{field.label}</FieldLabel>
                            <Checkbox>
                              <Input
                                type="checkbox"
                                id={`edit-field-${field.id}`}
                                checked={field.enabled}
                                onChange={() => handleRegistrationFieldToggle(field.id)}
                              />
                              <Label htmlFor={`edit-field-${field.id}`}>Enable</Label>
                            </Checkbox>
                          </FieldHeader>
                          <div>Type: {field.type}</div>
                          {field.required && <div>Required: Yes</div>}
                          
                          {field.options && field.options.length > 0 && (
                            <FieldOptions>
                              <div>Options:</div>
                              <OptionsList>
                                {field.options.map(option => (
                                  <OptionTag key={option}>{option}</OptionTag>
                                ))}
                              </OptionsList>
                            </FieldOptions>
                          )}
                        </FieldItem>
                      ))}
                    </FieldsGrid>
                  </>
                )}
              </RegistrationFieldsSection>
              
              {formError && <ErrorMessage>{formError}</ErrorMessage>}
              
              <SubmitButton type="submit" disabled={submitting}>
                {submitting ? 'Updating...' : 'Update Wheel'}
              </SubmitButton>
            </Form>
          </ModalContent>
        </Modal>
      )}

      {/* Share Modal */}
      {showShareModal && shareWheel && (
        <ModalOverlay>
          <ShareModal>
            <CloseButton onClick={() => setShowShareModal(false)}>
              <FontAwesomeIcon icon={faTimes} />
            </CloseButton>
            <ShareTitle>Share "{shareWheel.name}"</ShareTitle>
            
            <ShareLink>
              <ShareInput 
                type="text" 
                value={`${window.location.origin}/wheel/${shareWheel.id}`} 
                readOnly 
              />
              <CopyButton onClick={copyToClipboard}>
                <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
                {copied ? 'Copied!' : 'Copy'}
              </CopyButton>
            </ShareLink>
            
            <ShareOptions>
              <ShareOption 
                href={getShareUrl('whatsapp')} 
                target="_blank" 
                rel="noopener noreferrer"
                className="whatsapp"
              >
                <FontAwesomeIcon icon={faWhatsapp} />
                <ShareLabel>WhatsApp</ShareLabel>
              </ShareOption>
              
              <ShareOption 
                href={getShareUrl('facebook')} 
                target="_blank" 
                rel="noopener noreferrer"
                className="facebook"
              >
                <FontAwesomeIcon icon={faFacebook} />
                <ShareLabel>Facebook</ShareLabel>
              </ShareOption>
              
              <ShareOption 
                href={getShareUrl('twitter')} 
                target="_blank" 
                rel="noopener noreferrer"
                className="twitter"
              >
                <FontAwesomeIcon icon={faTwitter} />
                <ShareLabel>Twitter</ShareLabel>
              </ShareOption>
              
              <ShareOption 
                href={getShareUrl('linkedin')} 
                target="_blank" 
                rel="noopener noreferrer"
                className="linkedin"
              >
                <FontAwesomeIcon icon={faLinkedin} />
                <ShareLabel>LinkedIn</ShareLabel>
              </ShareOption>
            </ShareOptions>
          </ShareModal>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default AdminWheels; 